import differenceWith from 'lodash/differenceWith'
import flatten from 'lodash/flatten'
import isEqual from 'lodash/isEqual'
import {
  IApplicationRelationshipLevels,
  ICarrierRelationshipInput,
  ICarrierRelationshipTypes,
  ISetCarrierRelationshipsInput,
} from 'types/graphqlTypes'
import { ICarrierPageFragment } from '../../../../Carrier/graphql/CarrierPageFragment'
import { TAutocompleteValueSchema } from '../schema/AutocompleteValueSchema'
import { TCarrierRelationshipsModalSchema } from '../schema/CarrierRelationshipsModalSchema'

type TFormKey = keyof TCarrierRelationshipsModalSchema

const BASE_RELATIONSHIP_INPUT: Record<
  TFormKey,
  Pick<ICarrierRelationshipInput, 'relationshipLevel' | 'relationshipType'>
> = {
  primaryManager: {
    relationshipLevel: IApplicationRelationshipLevels.primary,
    relationshipType: ICarrierRelationshipTypes.carrier_manager,
  },
  secondaryManagers: {
    relationshipLevel: IApplicationRelationshipLevels.secondary,
    relationshipType: ICarrierRelationshipTypes.carrier_manager,
  },
  primaryExperienceRep: {
    relationshipLevel: IApplicationRelationshipLevels.primary,
    relationshipType: ICarrierRelationshipTypes.carrier_experience_representative,
  },
  secondaryExperienceRep: {
    relationshipLevel: IApplicationRelationshipLevels.secondary,
    relationshipType: ICarrierRelationshipTypes.carrier_experience_representative,
  },
  primarySalesRep: {
    relationshipLevel: IApplicationRelationshipLevels.primary,
    relationshipType: ICarrierRelationshipTypes.carrier_sales_representative,
  },
  secondarySalesReps: {
    relationshipLevel: IApplicationRelationshipLevels.secondary,
    relationshipType: ICarrierRelationshipTypes.carrier_sales_representative,
  },
}

const formDataToMutationInput = (
  key: keyof TCarrierRelationshipsModalSchema,
  value: TAutocompleteValueSchema | TAutocompleteValueSchema[]
): ICarrierRelationshipInput[] => {
  const baseRelationshipInput = BASE_RELATIONSHIP_INPUT[key]

  // secondary ce reps and sales reps are a list
  if (Array.isArray(value)) {
    return value.map(v => ({
      ...baseRelationshipInput,
      userId: v.id,
    }))
  }

  return [
    {
      ...baseRelationshipInput,
      userId: value.id,
    },
  ]
}

/**
 * @description Map form input to mutation input
 */
export const prepareSetCarrierRelationshipsInput = (
  formData: TCarrierRelationshipsModalSchema,
  carrierApplication: Pick<ICarrierPageFragment, 'carrierRelationships' | 'id'>
): ISetCarrierRelationshipsInput => {
  const { carrierRelationships, id: carrierApplicationId } = carrierApplication

  // Map existing carrier relationships to an input type list
  const existingRelationships: ICarrierRelationshipInput[] = carrierRelationships.map(cr => ({
    userId: cr.user.id,
    relationshipType: cr.relationshipType,
    relationshipLevel: cr.relationshipLevel,
  }))

  // Map form relationships to an input type list
  const formRelationships: ICarrierRelationshipInput[] = flatten(
    Object.entries(formData)
      .filter(([, value]) => Boolean(value))
      .map(([key, value]) => formDataToMutationInput(key as TFormKey, value!))
  )

  // i.e., form relationships that are not in the existing relationships
  const relationshipsToCreate = differenceWith(formRelationships, existingRelationships, isEqual)
  // i.e., existing relationships that are not in the form relationships
  const relationshipsToDelete = differenceWith(
    existingRelationships,
    formRelationships,
    isEqual
  ).map(cr => ({
    ...cr,
    destroy: true,
  }))

  return {
    carrierApplicationId,
    carrierRelationships: [...relationshipsToCreate, ...relationshipsToDelete],
  }
}
